import React, { useCallback, useRef, useState } from 'react';
import MaterialTable from 'material-table';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CheckCircleOutline, CancelOutlined } from '@material-ui/icons';
import CircleIcon from '@material-ui/icons/Brightness1';

import { localizationTable, options } from 'constants/table';
import { statusUserSolicitation } from 'constants/status';
import { EnrollmentData } from 'services/dataService';

const SolicitationStatus = {
  pending: 1,
  approved: 2,
  reproved: 3,
};

export default () => {
  const [isLoading, setIsLoading] = useState(true);
  const tableRef = useRef();
  const { t } = useTranslation(['tables', 'toastify', 'button']);

  const refreshTable = useCallback(() => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, []);

  const setSolicitation = async (solicitation, status) => {
    try {
      setIsLoading(true);
      await EnrollmentData.updateSolicitationEnrollment(solicitation.id, {
        status,
      });
      toast.success(t('toastify:commons.toast_update'));
      refreshTable();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('toastify:commons.toast_error_403'));
      } else {
        toast.error(t('toastify:commons.toast_error_api'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="notranslate">
      <MaterialTable
        title={t('tables:enrollment_solicitations.title')}
        tableRef={tableRef}
        columns={[
          {
            title: t('tables:enrollment_solicitations.name_column'),
            field: 'name',
            sorting: false,
            render: (rowData) => rowData.name,
          },
          {
            title: t('tables:enrollment_solicitations.email_column'),
            field: 'email',
            sorting: false,
            render: (rowData) => rowData.email,
          },
          {
            title: t('tables:enrollment_solicitations.course_column'),
            field: 'course',
            sorting: false,
            render: (rowData) =>
              rowData?.company_course?.course?.name ||
              rowData?.company_course?.name ||
              '-',
          },
          {
            title: t('tables:enrollment_solicitations.status_column'),
            field: 'status',
            sorting: false,
            render: (rowData) => (
              <div style={{ display: 'flex' }}>
                <CircleIcon
                  style={statusUserSolicitation[rowData.status]?.style}
                  fontSize="small"
                />
                {t(statusUserSolicitation[rowData.status]?.title)}
              </div>
            ),
          },
          {
            title: t('tables:enrollment_solicitations.solicitation_date_column'),
            field: 'createdAt',
            sorting: false,
            render: (rowData) =>
              format(parseISO(rowData.createdAt || rowData.created_at), 'dd/MM/yyyy'),
          },
        ]}
        data={async (query) => {
          try {
            const response = await EnrollmentData.getSolicitationEnrollments({
              page: query.page + 1,
              limit: query.pageSize,
              status: SolicitationStatus.pending,
            });

            setIsLoading(false);

            return {
              data: response.data.enrollmentsSolicitations,
              page: response.data.currentPage - 1,
              totalCount: response.data.totalCount,
            };
          } catch (error) {
            if (error.response?.status === 403) {
              toast.error(t('toastify:commons.toast_error_403'));
            } else {
              toast.error(t('toastify:commons.toast_error_api'));
            }

            setIsLoading(false);
            return { data: [] };
          }
        }}
        actions={[
          {
            icon: () => <CheckCircleOutline color="primary" />,
            tooltip: t('tables:enrollment_solicitations.actions.solicitation_approve'),
            position: 'row',
            onClick: (event, rowData) =>
              setSolicitation(rowData, SolicitationStatus.approved),
          },
          {
            icon: () => <CancelOutlined color="error" />,
            tooltip: t('tables:enrollment_solicitations.actions.solicitation_reprove'),
            position: 'row',
            onClick: (event, rowData) =>
              setSolicitation(rowData, SolicitationStatus.reproved),
          },
        ]}
        isLoading={isLoading}
        localization={localizationTable(t)}
        options={{
          ...options,
          selection: false,
        }}
      />
    </div>
  );
};
